import * as Sentry from '@sentry/react';
import { handleCreateLogs, handleCreateSchoologyAuth, handleSetSchoologyAuthWithsjwt, handleUpdateLogs } from 'store/schoologyauth/thunks';
import { handleLoginWithToken } from 'store/auth/thunks';
import { handleSchoologyLogin, handleSyncSchoologyClasses } from 'store/schoology/thunks';
import Schoology from 'utils/Schoology';

const local_store_keys = {
    search_item: "search_item",
    auth_info: "auth_info",
    log_id: "log_id"
}

const client = new Schoology();
function parseQueryToObject(query) {
    return Object.fromEntries(
      query.split("&").map(pair => {
        const [key, value] = pair.split("=");
        return [decodeURIComponent(key), decodeURIComponent(value)];
      })
    );
  }

export const handleSchoologyAuth = async ({ 
    search, 
    setIsAutoLogin, 
    setError, 
    setAutologinMessage,
    setLoading,
    loginWithSchoology,
    dispatch,
    firebaseContext
}) => {
    try {
        if (typeof search === "object" && (search.uid && search.sjwt)) {
            setLoading(true);
            setAutologinMessage('Please wait while auto-login is in progress.');
            setError('Please wait while auto-login is in progress.');
            setIsAutoLogin(true);
            await handleCreateLogs({
                url: window.location.href,
                user_id: search.uid,
                data: search
            })

            const jwtResponse = await handleSetSchoologyAuthWithsjwt(search.sjwt, search.uid);
            if (jwtResponse && jwtResponse.success) {
                const { data: logindata, auth, user, token, userData } = jwtResponse.data;
                localStorage.setItem(local_store_keys.search_item, JSON.stringify(logindata));
                if (user && user.id && logindata && auth) {
                    if (token) {
                        const result = await dispatch(handleLoginWithToken(token));
                        if (!result || !result.isSignedIn) {
                            setAutologinMessage('Authentication failed. Please try login again!');
                            setError('Authentication failed. Please try login again!');
                            setLoading(false);
                            setIsAutoLogin(false);
                            return;
                        }
                        setAutologinMessage(`User found. Syncing classes…`);
                        setError(`User found. Syncing classes…`);
                        await handleSyncSchoologyClasses(logindata.uid);
                        const loginUserData = userData ? { ...userData, username: result.username } : { username: result.username };
                        await loginWithSchoology(loginUserData);
                        setLoading(false);
                        setIsAutoLogin(false);
                    } else {
                        await handleOnSwjSuccessWithUserInfo({ logindata, auth, user }, { setAutologinMessage, setError, setLoading, setIsAutoLogin, loginWithSchoology, dispatch });
                    }
                } else if (auth && logindata) {
                    const response = await handleSchoologyLogin({
                        schoologyid: logindata.uid,
                        redirectItem: logindata
                    });
                    if (response?.success && response?.data?.token) {
                        const result = await dispatch(handleLoginWithToken(response.data.token));
                        if (!result || !result.isSignedIn) {
                            setAutologinMessage('Authentication failed. Please try login again!');
                            setError('Authentication failed. Please try login again!');
                            setLoading(false);
                            setIsAutoLogin(false);
                            return;
                        }
                        setAutologinMessage(`User found. Syncing classes…`);
                        setError(`User found. Syncing classes…`);
                        await handleSyncSchoologyClasses(logindata.uid);
                        const userData = response.data.data
                            ? { ...response.data.data, username: result.username }
                            : { username: result.username };
                        await loginWithSchoology(userData);
                        setLoading(false);
                        setIsAutoLogin(false);
                    } else if (response?.data?.error) {
                        await updateErrorLog(response.data, 'user_login_fail');
                        setAutologinMessage(response.data.error || 'Unable to login with Schoology. Please try again.');
                        setError(response.data.error || 'Unable to login with Schoology. Please try again.');
                        setLoading(false);
                        setIsAutoLogin(false);
                        return;
                    } else {
                        await requestTokenforSwjt(logindata, { setAutologinMessage, setError, setLoading, setIsAutoLogin, loginWithSchoology });
                    }
                } else if (logindata) {
                    await requestTokenforSwjt(logindata, { setAutologinMessage, setError, setLoading, setIsAutoLogin, loginWithSchoology });
                } else {
                    await updateErrorLog(logindata, 'user_login_fail');
                    setAutologinMessage('Unable to login with Schoology. Please try again.');
                    setError('Unable to login with Schoology. Please try again.');
                    setLoading(false);
                    setIsAutoLogin(false);
                    return;
                }
            } else {
                await updateErrorLog(jwtResponse, 'user_login_fail');
                const rawErrorMessage = jwtResponse?.message || jwtResponse?.error || jwtResponse?.data?.error || '';
                const schoologyJwtErrorMessage = typeof rawErrorMessage === "string" && rawErrorMessage.toLowerCase().includes('signature expired')
                    ? 'Schoology login link expired. Please open Schoology again and try login.'
                    : (rawErrorMessage || 'Unable to authenticate with Schoology. Please try again.');
                setAutologinMessage(schoologyJwtErrorMessage);
                setError(schoologyJwtErrorMessage);
                setLoading(false);
                setIsAutoLogin(false);
            }
        } else if (typeof search === "object" && search.oauth_token) {
            setLoading(true);
            setIsAutoLogin(true);
            await onAuthApproved(search, { setAutologinMessage, setError, setLoading, setIsAutoLogin, loginWithSchoology, dispatch, firebaseContext });
        }
    } catch (error) {
        Sentry.captureException(`596 Error in handleSchoologyAuth: ${error}`);
        setAutologinMessage('There was an error while processing the auto-login. Please try again or contact support for assistance.');
        setError('There was an error while processing the auto-login. Please try again or contact support for assistance.');
        setLoading(false);
        setIsAutoLogin(false);
    }
};

const getSchoology = async (search, { setAutologinMessage, setError, setLoading, setIsAutoLogin, loginWithSchoology, dispatch }) => {
    try {
        if (typeof search === "object" && (search.uid && search.oauth_token)) {
            const UID = search.uid;
            setLoading(true);
            setIsAutoLogin(true);
            const searchItem = search.school_nid ? search : JSON.parse(localStorage.getItem(local_store_keys.search_item) ?? {});
            const redirectItem = {
                building_nid: searchItem.building_nid,
                building_title: searchItem.building_title,
                school_nid: searchItem.school_nid,
                school_title: searchItem.school_title,
                ...searchItem
            };
            const response = await handleSchoologyLogin({
                schoologyid: UID,
                redirectItem
            });
            if (response?.success && response?.data?.token) {
                const result = await dispatch(handleLoginWithToken(response.data.token));
                if (!result || !result.isSignedIn) {
                    setAutologinMessage('Authentication failed. Please try login again!');
                    setError('Authentication failed. Please try login again!');
                    setLoading(false);
                    setIsAutoLogin(false);
                    return;
                }
                setAutologinMessage(`User found. Syncing classes…`);
                setError(`User found. Syncing classes…`);
                await handleSyncSchoologyClasses(UID);
                const userData = response.data.data
                    ? { ...response.data.data, username: result.username }
                    : { username: result.username };
                await loginWithSchoology(userData);
                setLoading(false);
                setIsAutoLogin(false);
            } else {
                await updateErrorLog(response, 'user_login_fail_after_auth');
                setAutologinMessage(response?.data?.error || 'Unable to login with Schoology. Please try again.');
                setError(response?.data?.error || 'Unable to login with Schoology. Please try again.');
                setLoading(false);
                setIsAutoLogin(false);
                return;
            }
        } else {
            setLoading(false);
            setIsAutoLogin(false);
        }
    } catch (error) {
        Sentry.captureException(`595 Error in getSchoology: ${error}`);
        setLoading(false);
        setIsAutoLogin(false);
        console.log(error, "eeee");
    }
};

const requestTokenforSwjt = async (logindata, { setAutologinMessage, setError, setLoading, setIsAutoLogin}) => {
    try {
        if (!logindata.oauth_token) {
            let data = await client.getRequestToken();
            if(typeof data === "string"){
                data = parseQueryToObject(data);
            }
            localStorage.setItem(local_store_keys.auth_info, JSON.stringify(data));
            localStorage.setItem(local_store_keys.search_item, JSON.stringify(logindata));
            if(!data||!data.oauth_token){
                await updateErrorLog(data, 'user_login_fail');
                setAutologinMessage('Unable to initiate Schoology authentication. Please try again.');
                setError('Unable to initiate Schoology authentication. Please try again.');
                setLoading(false);
                setIsAutoLogin(false);
                return;
            }
            
            const callback = `${window.location.origin}/?uid=${logindata.uid}&role_id=${logindata.role_id}&school_nid=${logindata.school_nid}&school_title=${logindata.school_title}`;
            const domain = logindata.domain ? logindata.domain : "app.schoology.com";
            const _url = `https://${domain}/oauth/authorize?oauth_token=${data.oauth_token}&oauth_callback=${callback}`;
            popitup(_url, "login");
        }else{
            setAutologinMessage('Unable to initiate Schoology authentication. Please try again.');
            setError('Unable to initiate Schoology authentication. Please try again.');
            setLoading(false);
            setIsAutoLogin(false);
        }
    } catch (error) {
        Sentry.captureException(`598 Error in requestTokenforSwjt: ${error}`);
        setAutologinMessage('Unable to initiate Schoology authentication. Please try again.');
        setError('Unable to initiate Schoology authentication. Please try again.');
        setLoading(false);
        setIsAutoLogin(false);
    }
};

export const onAuthApproved = async (search, { setAutologinMessage, setError, setLoading, setIsAutoLogin, loginWithSchoology, dispatch }) => {
    try {
        const authInfo = JSON.parse(localStorage.getItem(local_store_keys.auth_info));
        const _data = await client.getAccessToken(authInfo);
        const UID = search.uid;
        updateSuccessLog(_data,"authenticated");
        
        if (_data.oauth_token && _data.oauth_token_secret) {
            const stored = await dispatch(handleCreateSchoologyAuth({
                oauth_token: _data.oauth_token,
                oauth_token_secret: _data.oauth_token_secret,
                id: UID
            }));
            if (!stored) {
                await updateErrorLog(_data, 'user_login_auth_fail');
                setAutologinMessage('Unable to save Schoology credentials. Please try again.');
                setError('Unable to save Schoology credentials. Please try again.');
                setLoading(false);
                setIsAutoLogin(false);
                return;
            }
            await getSchoology(search, { setAutologinMessage, setError, setLoading, setIsAutoLogin, loginWithSchoology, dispatch });
        } else {
            await updateErrorLog(_data, 'user_login_auth_fail');
            setAutologinMessage('Unable to login with Schoology. Please try again.');
            setError('Unable to login with Schoology. Please try again.');
            setLoading(false);
            setIsAutoLogin(false);
            return;
        }
    } catch (error) {
        Sentry.captureException(`599 Error in onAuthApproved: ${error}`);
        setAutologinMessage('Authentication approval failed. Please try again.');
        setError('Authentication approval failed. Please try again.');
        setLoading(false);
        setIsAutoLogin(false);
    }
};



const handleOnSwjSuccessWithUserInfo = async (userdata, { setAutologinMessage, setError, setLoading, setIsAutoLogin, loginWithSchoology, dispatch }) => {
    try {
        const { user, logindata } = userdata || {};
        if (user && user.id && logindata) {
            const response = await handleSchoologyLogin({
                schoologyid: logindata.uid,
                redirectItem: logindata
            });
            if (response?.success && response?.data?.token) {
                const result = await dispatch(handleLoginWithToken(response.data.token));
                if (!result || !result.isSignedIn) {
                    setAutologinMessage('Authentication failed. Please try login again!');
                    setError('Authentication failed. Please try login again!');
                    setLoading(false);
                    setIsAutoLogin(false);
                    return;
                }
                updateSuccessLog(logindata);
                await Promise.all([
                    handleSyncSchoologyClasses(logindata.uid),
                    loginWithSchoology(response.data.data ? { ...response.data.data, username: result.username } : { username: result.username })
                ]);
                setLoading(false);
                setIsAutoLogin(false);
            } else {
                await updateErrorLog(response, 'user_login_with_info_fail');
                setAutologinMessage(response?.data?.error || 'Unable to login with Schoology. Please try again.');
                setError(response?.data?.error || 'Unable to login with Schoology. Please try again.');
                setLoading(false);
                setIsAutoLogin(false);
                return;
            }
        } else {
            await updateErrorLog(user, 'user_login_with_info_fail');
            setAutologinMessage('Unable to login with Schoology. Please try again.');
            setError('Unable to login with Schoology. Please try again.');
            setLoading(false);
            setIsAutoLogin(false);
            return;
        }
    } catch (error) {
        Sentry.captureException(`600 Error in handleOnSwjSuccessWithUserInfo: ${error}`);
        setAutologinMessage('Unable to login with Schoology. Please try again.');
        setError('Unable to login with Schoology. Please try again.');
        setLoading(false);
        setIsAutoLogin(false);
    }
};

const updateSuccessLog = (logindata , status) => {
    const last_log_id = localStorage.getItem(local_store_keys.log_id);
    if (last_log_id) {
        return handleUpdateLogs({
            id: last_log_id,
            login_status: status || "user_login_success",
            error: null,
            updates: logindata
        });
    }
}

const updateErrorLog = (message, status) => {
    const last_log_id = localStorage.getItem(local_store_keys.log_id);
    if (last_log_id) {
        return handleUpdateLogs({
            id: last_log_id,
            login_status: status,
            error: message,
            updates: null
        });
    }
}

function popitup(url, windowName) {
    window.open(url, "_self");
    return false;
}